import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Section } from '@/components/fleetguardian';
import { Skeleton } from '@/components/ui/skeleton';
import { useFraudHubTopRules } from '@/api/hooks/useFraudHubTopRules';
import {
  CHART_GRID_STROKE,
  chartAxis,
  chartTooltipItemStyle,
  chartTooltipLabelStyle,
  chartTooltipStyle,
} from '@/lib/chartTheme';
import { fmtNum } from '@/components/fleetguardian/format';
import { SEVERITY_FILL } from '@/lib/severity';
import { parsePeriod } from '@/lib/timePeriod';
import type { Severity } from '@/types/alerts';

const NEUTRAL_FILL = 'hsl(215 20% 65%)';

function severityFill(sev: string): string {
  return (SEVERITY_FILL as Record<string, string>)[sev] ?? NEUTRAL_FILL;
}

/**
 * Fraud dashboard top-rules bar (Wave 2C) — horizontal bar of period fire-count
 * per rule from `/fraud-hub/top-rules`, each bar tinted by the rule's worst
 * severity. Distinct from the Fraud Hub's rule-card grid: this is the
 * chart-forward analytics view. Click a bar to open that rule's worklist.
 */
export function TopRulesBar(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const period = parsePeriod(searchParams.get('period'), 'M');
  const { data, isLoading, isError, error } = useFraudHubTopRules(period);

  const label = data?.period_label ? ` — ${data.period_label}` : '';
  const rules = data?.rules ?? [];
  const hasData = rules.some((r) => r.total_24h > 0);
  const height = Math.max(200, rules.length * 34 + 48);

  return (
    <Section
      title={`Top Firing Rules${label}`}
      subtitle="Bar height = fires in the period; colour = worst severity. Click to open the worklist."
    >
      {isLoading && <Skeleton className="h-[240px] w-full" />}
      {!isLoading && isError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          Failed to load top rules: {error?.message ?? 'unknown error'}
        </div>
      )}
      {!isLoading && !isError && !hasData && (
        <div className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">
          No rule activity in this window.
        </div>
      )}
      {!isLoading && !isError && hasData && (
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={rules}
              margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
            >
              <CartesianGrid horizontal={false} stroke={CHART_GRID_STROKE} />
              <XAxis type="number" {...chartAxis} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="rule_id"
                {...chartAxis}
                width={90}
                tick={{ ...chartAxis.tick, fontSize: 11 }}
              />
              <Tooltip
                cursor={{ fill: 'rgba(31,107,117,0.06)' }}
                contentStyle={chartTooltipStyle}
                itemStyle={chartTooltipItemStyle}
                labelStyle={chartTooltipLabelStyle}
                formatter={(value: number, _n, entry) => [
                  fmtNum(value),
                  (entry?.payload as { severity?: string })?.severity ?? 'Fires',
                ]}
              />
              <Bar
                dataKey="total_24h"
                name="Fires"
                radius={[0, 4, 4, 0]}
                isAnimationActive={false}
                cursor="pointer"
                onClick={(_e, index) => {
                  const rule = rules[index];
                  if (rule) navigate(`/fraud?rule_id=${encodeURIComponent(rule.rule_id)}`);
                }}
              >
                {rules.map((r) => (
                  <Cell key={r.rule_id} fill={severityFill(r.severity as Severity)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Section>
  );
}

export default TopRulesBar;
